/**
 * Final Payout Letter - read, check, confirm.
 *
 * 5 Nov 2026. Payment release on an encumbered private-seller deal requires the payout
 * amount, who it goes to and their bank details (encumbranceService.payoutGate), and the
 * refusal says "Upload it on Purchase Details". Nothing on Purchase Details captured them,
 * so the first financed deal would have stopped at release with no way forward.
 *
 * This panel reads the financier's letter (the one uploaded as a DD document, or one
 * attached here), pre-fills the figures, and stores them only when a person with DD
 * review or first-approval rights presses Confirm. The two-person payment approval still
 * sees the payout line before any money leaves.
 */
import { useEffect, useState } from 'react'
import { Deal, User } from '../../types'
import { supabase } from '../../lib/supabase'
import { hasPermission } from '../../lib/permissions'
import FileDropZone from '../FileDropZone'
import { extractText } from '../../services/supplierInvoiceService'
import { loadPayoutLetter, loadResolution } from '../../services/encumbranceService'
import type { FinalPayoutLetter } from '../../services/encumbranceService'
import { parsePayoutLetter } from '../../lib/payoutParse'
import type { PayoutLetterRead } from '../../lib/payoutParse'
import './PayoutLetterPanel.css'

interface Props {
  deal: Deal
  user: User | null
  onChanged?: () => void
}

interface LetterDoc {
  id: string
  path: string
  name: string | null
  approved: boolean
}

const EMPTY = { amount: '', recipient: '', bsb: '', account: '', accountName: '' }
const money = (n: number) => '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function PayoutLetterPanel({ deal, user, onChanged }: Props) {
  const d = deal as unknown as Record<string, unknown>
  const [relevant, setRelevant] = useState(false)
  const [record, setRecord] = useState<FinalPayoutLetter | null>(null)
  const [doc, setDoc] = useState<LetterDoc | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [read, setRead] = useState<PayoutLetterRead | null>(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<{ text: string; tone: 'info' | 'error' } | null>(null)

  const canConfirm = hasPermission(user, 'perm-review-dd') || hasPermission(user, 'perm-1st-approval')

  const loadDoc = async (): Promise<LetterDoc | null> => {
    const { data } = await supabase
      .from('dd_documents')
      .select('id, document_file_path, original_filename, review_decision')
      .eq('deal_id', deal.id)
      .in('document_type', ['PAYOUT', 'finance_payout_letter'])
      .is('deleted_at', null)
      .order('uploaded_at', { ascending: false })
      .limit(1)
    const r = data && data[0]
    return r
      ? { id: String(r.id), path: String(r.document_file_path), name: (r.original_filename as string) || null, approved: r.review_decision === 'approved' }
      : null
  }

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const [res, rec, letter] = await Promise.all([loadResolution(deal.id), loadPayoutLetter(deal.id), loadDoc()])
        if (!live) return
        setRecord(rec)
        setDoc(letter)
        setRelevant(
          Boolean(rec || letter) ||
            res?.resolutionPath === 'PayoutLetterSplit' ||
            d.finance_payout_applicable === true ||
            d.seller_finance_payout_applicable === true,
        )
      } catch {
        /* Not being able to read the record is not proof there isn't one; show the panel. */
        if (live) setRelevant(true)
      }
    })()
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id])

  if (!relevant) return null

  /* Read a letter (the stored one, or a file just attached) and pre-fill the boxes. */
  const readLetter = async (file: File) => {
    setBusy(true)
    setNote(null)
    try {
      const text = await extractText(file)
      if (!text.trim()) {
        setNote({ text: 'The letter could not be read. Enter the figures from it by hand.', tone: 'error' })
        setEditing(true)
        return
      }
      const r = parsePayoutLetter(text)
      setRead(r)
      setForm({
        amount: r.amount !== null ? r.amount.toFixed(2) : '',
        recipient: r.recipient || r.accountName || '',
        bsb: r.bsb || '',
        account: r.accountNumber || '',
        accountName: r.accountName || r.recipient || '',
      })
      setEditing(true)
      setNote({
        text: r.found.length
          ? 'Read from the letter: ' + r.found.join(', ') + '. Check every figure against the letter before confirming.'
          : 'Nothing could be read off the letter. Enter the figures by hand.',
        tone: r.found.length ? 'info' : 'error',
      })
    } finally {
      setBusy(false)
    }
  }

  const readStored = async () => {
    if (!doc) return
    setBusy(true)
    try {
      const { data, error } = await supabase.storage.from('dd-documents').download(doc.path)
      if (error || !data) throw error || new Error('The letter could not be opened.')
      const name = doc.name || doc.path.split('/').pop() || 'payout-letter'
      await readLetter(new File([data], name, { type: data.type || '' }))
    } catch (e) {
      setNote({ text: e instanceof Error ? e.message : 'The letter could not be opened.', tone: 'error' })
      setBusy(false)
    }
  }

  /* Attach a letter here when none was uploaded as a DD document. It is filed as one, so
     it goes to DD Review for approval like any other - the gate to Paid still needs that. */
  const attach = async (file?: File) => {
    if (!file) return
    setBusy(true)
    setNote(null)
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = `dd-docs/${deal.id}/finance_payout_letter-${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from('dd-documents').upload(path, file)
      if (upErr) throw upErr
      const { error: rowErr } = await supabase.from('dd_documents').insert({
        deal_id: deal.id,
        document_type: 'finance_payout_letter',
        document_file_path: path,
        uploaded_by_role: 'staff',
        uploaded_via: 'workspace',
        uploaded_at: new Date().toISOString(),
        original_filename: String(file.name).slice(0, 255),
        file_size_bytes: file.size,
        content_type: file.type || null,
        validation_status: 'manual_review',
        manual_review_required: true,
        manual_review_reason: 'Payout letter attached on Purchase Details. Approve it in DD Review.',
      })
      if (rowErr) throw rowErr
      setDoc(await loadDoc())
      await readLetter(file)
    } catch (e) {
      setNote({ text: e instanceof Error ? e.message : 'The letter could not be attached.', tone: 'error' })
      setBusy(false)
    }
  }

  const confirm = async () => {
    const amount = Number(String(form.amount).replace(/[$,\s]/g, ''))
    const bsb = form.bsb.replace(/\D/g, '')
    const account = form.account.replace(/\D/g, '')
    const problems: string[] = []
    if (!(amount > 0)) problems.push('a payout amount')
    if (!form.recipient.trim()) problems.push('who the payout goes to')
    if (bsb.length !== 6) problems.push('a six-digit BSB')
    if (account.length < 5 || account.length > 9) problems.push('an account number of 5 to 9 digits')
    if (problems.length) {
      setNote({ text: 'Still needed before this can be confirmed: ' + problems.join(', ') + '.', tone: 'error' })
      return
    }
    if (!doc) {
      setNote({ text: 'Attach the payout letter first. The figures must come from a document on file.', tone: 'error' })
      return
    }
    setBusy(true)
    setNote(null)
    try {
      const values = {
        deal_id: deal.id,
        document_path: doc.path,
        document_name: doc.name,
        payout_amount: amount,
        payout_recipient: form.recipient.trim(),
        payout_recipient_bank_bsb: bsb.slice(0, 3) + '-' + bsb.slice(3),
        payout_recipient_bank_acct: account,
        payout_recipient_bank_name: form.accountName.trim() || form.recipient.trim(),
        uploaded_at: new Date().toISOString(),
        uploaded_by: user?.id ?? null,
      }
      const { error } = await supabase.from('final_payout_letters').upsert(values, { onConflict: 'deal_id' })
      if (error) throw error

      /* The loan reference and the letter's date belong on the deal; fill them if empty. */
      const dealPatch: Record<string, unknown> = {}
      if (read?.reference && !String(d.payout_reference || '').trim()) dealPatch.payout_reference = read.reference
      /* The letter's own date, never its expiry: the Bill of Sale prints it as the date of the letter. */
      if (read?.letterDate && !d.payout_letter_date) dealPatch.payout_letter_date = read.letterDate
      if (Object.keys(dealPatch).length) await supabase.from('deals').update(dealPatch).eq('id', deal.id)

      await supabase.from('workflow_logs').insert({
        deal_id: deal.id,
        trigger_name: 'PAYOUT_LETTER_CONFIRMED',
        trigger_status: 'success',
        success: true,
        actor_user_id: user?.id ?? null,
        executed_at: new Date().toISOString(),
        details: {
          payout_amount: amount,
          payout_recipient: values.payout_recipient,
          bsb: values.payout_recipient_bank_bsb,
          account_last4: account.slice(-4),
          read_from_letter: read ? read.found : [],
          replaced_previous: Boolean(record),
        },
      })

      setRecord(await loadPayoutLetter(deal.id))
      setEditing(false)
      setNote({ text: 'Payout figures confirmed. The payment run will pay the financier first and the seller the balance.', tone: 'info' })
      onChanged?.()
    } catch (e) {
      setNote({ text: e instanceof Error ? e.message : 'The payout figures could not be saved.', tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const price = Number(d.valuation_amount) || 0
  const formAmount = Number(String(form.amount).replace(/[$,\s]/g, '')) || 0
  const daysLeft = read?.validUntil ? Math.ceil((Date.parse(read.validUntil) - Date.now()) / 864e5) : null

  return (
    <div className="payout-panel">
      <div className="payout-head">
        <span className="ws-label">Final payout letter</span>
        <span className={'payout-chip ' + (record ? 'is-done' : 'is-open')}>{record ? 'Confirmed' : 'Needed'}</span>
      </div>

      {record && !editing && (
        <div className="payout-summary">
          <div><span>Payout</span><strong>{money(record.payoutAmount || 0)}</strong></div>
          <div><span>To</span><strong>{record.payoutRecipient}</strong></div>
          <div><span>BSB / account</span><strong>{record.bankBsb} {record.bankAccount ? '••••' + record.bankAccount.slice(-4) : ''}</strong></div>
          <div><span>Balance to seller</span><strong>{money(Math.max(0, price - (record.payoutAmount || 0)))}</strong></div>
          {canConfirm && (
            <button type="button" className="payout-btn payout-btn-ghost" disabled={busy} onClick={() => {
              setForm({
                amount: record.payoutAmount ? record.payoutAmount.toFixed(2) : '',
                recipient: record.payoutRecipient || '',
                bsb: record.bankBsb || '',
                account: record.bankAccount || '',
                accountName: record.bankName || '',
              })
              setEditing(true)
            }}>Change</button>
          )}
        </div>
      )}

      {!record && !editing && (
        <div className="payout-start">
          {doc ? (
            <>
              <p>
                The financier&rsquo;s letter is on file{doc.approved ? ' and approved in DD Review' : ' (not yet approved in DD Review)'}.
                Read it to fill in the payout figures.
              </p>
              <button type="button" className="payout-btn payout-btn-primary" disabled={busy || !canConfirm} onClick={readStored}>
                {busy ? 'Reading the letter…' : 'Read the payout letter'}
              </button>
            </>
          ) : (
            <>
              <p>There is finance owing on this vehicle. Attach the financier&rsquo;s payout letter and the figures are read from it.</p>
              <FileDropZone
                accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                label="Attach payout letter"
                busy={busy}
                busyLabel="Reading the letter…"
                disabled={!canConfirm}
                onFiles={(files) => attach(files[0])}
              />
            </>
          )}
          {!canConfirm && <small className="field-hint">Confirming payout figures needs DD review or first-approval rights.</small>}
        </div>
      )}

      {editing && (
        <div className="payout-form">
          <label>Payout amount ($)<input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputMode="decimal" /></label>
          <label>Paid to (financier)<input value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} /></label>
          <label>BSB<input value={form.bsb} onChange={(e) => setForm({ ...form, bsb: e.target.value })} placeholder="000-000" /></label>
          <label>Account number<input value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} /></label>
          <label className="payout-wide">Account name<input value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} /></label>

          {read?.bpayBiller && !read.bsb && (
            <div className="payout-warn payout-wide">
              This letter asks for payment by BPAY (biller {read.bpayBiller}{read.bpayReference ? ', reference ' + read.bpayReference : ''}).
              The bank file can only pay a BSB and account, so ask the financier for their account details, or pay this line by BPAY by hand.
            </div>
          )}
          {daysLeft !== null && daysLeft <= 3 && (
            <div className="payout-warn payout-wide">
              {daysLeft < 0 ? 'This payout figure expired on ' : 'This payout figure is only good until '}{read?.validUntil}. Ask the financier for an updated letter if payment will be later.
            </div>
          )}
          {price > 0 && formAmount > price && (
            <div className="payout-warn payout-wide">
              The payout ({money(formAmount)}) is more than the purchase price ({money(price)}). Only the purchase price is paid out; the seller owes the financier the difference.
            </div>
          )}

          <div className="payout-actions payout-wide">
            <button type="button" className="payout-btn payout-btn-primary" disabled={busy || !canConfirm} onClick={confirm}>{busy ? 'Saving…' : 'Confirm payout figures'}</button>
            <button type="button" className="payout-btn payout-btn-ghost" disabled={busy} onClick={() => { setEditing(false); setNote(null) }}>Cancel</button>
          </div>
        </div>
      )}

      {note && <div className={'payout-note ' + (note.tone === 'error' ? 'is-error' : '')}>{note.text}</div>}
    </div>
  )
}
